import React from 'react'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer } from 'recharts'

import { ChartTooltip, XAxis, YAxis } from '../../../Visualisation/components'
import { getAxisDomain, areDatesEqual, getMonthlyInterval } from '../../../Visualisation/helpers'
import { GraphPayload } from '../../../Visualisation/types'
import { useMediaQuery } from '@src/containers/Visualisation'

type Props = {
  graphHeight?: number
  data?: {
    budget: GraphPayload
    quantity: GraphPayload
  }
}

const BudgetAndQuantityChart: React.FC<Props> = ({ data, graphHeight }) => {
  const displayType = useMediaQuery()

  const formattedData = data?.budget?.map(({ date, value }) => ({
    date,
    budget: Number(value).toFixed(0),
    quantity: Number(
      data?.quantity.find(element => areDatesEqual(element.date, date))?.value
    ).toFixed(1)
  }))

  const budgetAxisDomain = getAxisDomain(formattedData, 'budget')
  const quantityAxisDomain = getAxisDomain(formattedData, 'quantity')

  const monthlyInterval = () => {
    switch (displayType) {
      case 'mobile':
        return getMonthlyInterval(13, data?.budget)
      default:
        return getMonthlyInterval(25, data?.budget)
    }
  }

  return (
    <ResponsiveContainer width={'99%'} height={graphHeight}>
      <AreaChart data={formattedData} margin={{ left: -20, top: 0 }}>
        <defs>
          <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
            <stop stopColor="#C88CF2" stopOpacity={0.2} />
            <stop offset="1" stopColor="#C88CF2" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
            <stop stopColor="#3A64F8" stopOpacity={0.2} />
            <stop offset="1" stopColor="#3A64F8" stopOpacity={0} />
          </linearGradient>
        </defs>
        {XAxis({ monthFormat: 'three', monthlyInterval: monthlyInterval() })}
        {YAxis({ dataKey: 'budget', name: 'budget', domain: budgetAxisDomain, unit: '₽' })}
        {YAxis({
          dataKey: 'quantity',
          name: 'quantity',
          yAxisId: 'quantity',
          orientation: 'right',
          minTickGap: 50,
          domain: quantityAxisDomain
        })}
        <CartesianGrid stroke="#E0E3EB" strokeDasharray="5 5" />
        {ChartTooltip({ cursor: { stroke: '#292E3D', strokeDasharray: '5 5' } })}
        <Area
          type="monotone"
          strokeWidth={2}
          fillOpacity={1}
          activeDot={{ r: 7.5, strokeWidth: 2 }}
          dataKey="budget"
          stroke="#C88CF2"
          fill="url(#colorUv)"
          unit="₽"
        />
        <Area
          type="monotone"
          strokeWidth={2}
          fillOpacity={1}
          activeDot={{ r: 7.5, strokeWidth: 2 }}
          dataKey="quantity"
          stroke="#3A64F8"
          fill="url(#colorPv)"
          yAxisId="quantity"
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

export default BudgetAndQuantityChart
